// ============================================================================
// useAPITrima — All Trima API calls in one place
// ============================================================================
// Mock-driven fallback is preserved behind TRIMA_USE_MOCKS.
// Flip to false (already done) to hit the real BE.
//
// Real endpoints (all under API.BASEPATH):
//   GET  /questions_competence/questionnaire/:userId     → loadRanking
//   POST /answers_competence/questionnaire/:userId       → postRanking  (returns cadran 1)
//   POST /answers_cadran_competence/questionnaire/:userId → postCadran
// ============================================================================

import { useContext } from 'react';
import useFetch from 'hooks/api/useFetch';
import { ToastContext, ToastContextProps } from 'contexts/ToastContext';
import { useTranslation } from 'react-i18next';
import { UserContext, UserContextProps } from 'contexts/UserContext';
import { useRollbar } from '@rollbar/react';
import { API } from 'constants/global';

import { TRIMA_USE_MOCKS, CADRAN_MIN_SELECTIONS } from 'pages/questionnaire-trima/constants/trima.constants';
import {
  LocalizedLabel,
  TrimaRankingPayload,
  TrimaRankingResponse,
  TrimaCadranPayload,
  TrimaCadranResponse,
  TrimaCadranPostResponse,
  TrimaCadranNumber,
} from 'pages/questionnaire-trima/types/trima.types';
import {
  MOCK_RANKING_PAYLOAD,
  MOCK_CADRAN_1,
  mockCadranPostResponse,
} from 'pages/questionnaire-trima/mocks/trima.mocks';

const mockDelay = <T,>(value: T, ms = 600): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

// ----------------------------------------------------------------------------
// BE response parsers
// ----------------------------------------------------------------------------

function parseTranslations(
  translations: Array<{ locale: string; libelle: string }>,
): LocalizedLabel {
  return translations.reduce(
    (acc, t) => ({ ...acc, [t.locale]: t.libelle }),
    {} as LocalizedLabel,
  );
}

function parseRankingPayload(data: any): TrimaRankingPayload {
  return {
    questionnaireId: data.questionnaire.id,
    questions: data.questions.map((q: any, idx: number) => ({
      id: q.id,
      order: idx + 1,
      label: parseTranslations(q.translations),
      answers: q.response_items.map((item: any, itemIdx: number) => ({
        id: item.id,
        label: parseTranslations(item.translations),
        initialRank: itemIdx + 1,
      })),
    })),
  };
}

function parseCadranPayload(
  cadranNumber: TrimaCadranNumber,
  cadranItems: any[],
  sessionCompetenceId: number,
): TrimaCadranPayload {
  return {
    cadranNumber,
    sessionCompetenceId,
    minSelections: CADRAN_MIN_SELECTIONS[cadranNumber],
    items: cadranItems.map((item: any) => ({
      id: item.id,
      label: parseTranslations(item.translations),
    })),
  };
}

// ----------------------------------------------------------------------------
// Interface
// ----------------------------------------------------------------------------

export interface IUseAPITrima {
  /** Load the 24 ranking questions for phase 1. */
  loadRanking: () => Promise<TrimaRankingPayload | null>;

  /**
   * Submit all 24×5 ranking responses. The BE responds with cadran 1's
   * payload directly — no separate load call needed.
   */
  postRanking: (
    questionnaireId: number,
    responses: TrimaRankingResponse[],
  ) => Promise<TrimaCadranPayload | null>;

  /**
   * Submit a cadran's selections. BE responds with one of:
   *   - 'refine' + new payload (same cadranNumber, the user re-selects)
   *   - 'next'   + new payload (next cadranNumber)
   *   - 'done'             (all 4 cadrans validated)
   *
   * TODO: verify against staging that the response includes `current_cadran`
   * so we can distinguish refine vs next. Adjust parseCadranPostResponse if
   * the field name differs.
   */
  postCadran: (response: TrimaCadranResponse) => Promise<TrimaCadranPostResponse | null>;
}

// ----------------------------------------------------------------------------
// Hook
// ----------------------------------------------------------------------------

function useAPITrima(): IUseAPITrima {
  const { setToast } = useContext(ToastContext) as ToastContextProps;
  const { user } = useContext(UserContext) as UserContextProps;
  const { getRequest, hideLoading } = useFetch<any>();
  const { t } = useTranslation('trima');
  const rollbar = useRollbar();

  // ---------------------------------------------------------------------
  // PHASE 1 — RANKING
  // ---------------------------------------------------------------------

  const loadRanking = async (): Promise<TrimaRankingPayload | null> => {
    try {
      if (TRIMA_USE_MOCKS) {
        return await mockDelay(MOCK_RANKING_PAYLOAD);
      }
      const result = await getRequest({
        url: `${API.ROUTES.TRIMA.QUESTIONS}/${user?.id}`,
        method: 'get',
      });
      return parseRankingPayload(result.data.data);
    } catch (e) {
      console.error('[useAPITrima.loadRanking] failed:', e);
      rollbar.error('Trima: loadRanking failed', { error: e, userId: user?.id });
      setToast({ message: t('errors.load-ranking'), status: 'danger' });
      return null;
    } finally {
      hideLoading();
    }
  };

  const postRanking = async (
    questionnaireId: number,
    responses: TrimaRankingResponse[],
  ): Promise<TrimaCadranPayload | null> => {
    try {
      if (TRIMA_USE_MOCKS) {
        await mockDelay(null);
        // Mock: cadran 1 has a placeholder session ID of 0.
        return { ...MOCK_CADRAN_1, sessionCompetenceId: 0 };
      }
      const result = await getRequest({
        url: `${API.ROUTES.TRIMA.POST_RANKING}/${user?.id}`,
        method: 'post',
        data: {
          questionnaire_id: questionnaireId,
          answers: responses.map((r) => ({
            question_id: r.questionId,
            ordered_items: r.rankedAnswerIds,
          })),
        },
      });
      const { reponses } = result.data.data;
      return parseCadranPayload(1, reponses.cadran_items, reponses.session_competence_id);
    } catch (e) {
      console.error('[useAPITrima.postRanking] failed:', e);
      rollbar.error('Trima: postRanking failed', { error: e, userId: user?.id });
      setToast({ message: t('errors.post-ranking'), status: 'danger' });
      return null;
    } finally {
      hideLoading();
    }
  };

  // ---------------------------------------------------------------------
  // PHASE 2 — CADRANS
  // ---------------------------------------------------------------------

  const postCadran = async (
    response: TrimaCadranResponse,
  ): Promise<TrimaCadranPostResponse | null> => {
    try {
      if (TRIMA_USE_MOCKS) {
        const cadranMinByNumber: Record<TrimaCadranNumber, number> = {
          1: 10, 2: 5, 3: 10, 4: 5,
        };
        const min = cadranMinByNumber[response.cadranNumber];
        const mockResp = mockCadranPostResponse(
          response.cadranNumber,
          response.selectedItemIds.length,
          min,
        );
        return await mockDelay(mockResp);
      }
      const result = await getRequest({
        url: `${API.ROUTES.TRIMA.POST_CADRAN}/${user?.id}`,
        method: 'post',
        data: {
          session_competence_id: response.sessionCompetenceId,
          current_cadran: response.cadranNumber,
          selected: response.selectedItemIds,
        },
      });
      return parseCadranPostResponse(response.cadranNumber, result.data.data);
    } catch (e) {
      console.error('[useAPITrima.postCadran] failed:', e);
      rollbar.error('Trima: postCadran failed', { error: e, userId: user?.id });
      setToast({ message: t('errors.post-cadran'), status: 'danger' });
      return null;
    } finally {
      hideLoading();
    }
  };

  return { loadRanking, postRanking, postCadran };
}

// ----------------------------------------------------------------------------
// Cadran POST response parser
// ----------------------------------------------------------------------------
// TODO: verify the exact response shape against staging. Assumptions:
//   - result.data.data = { session_competence_id, reponses: { current_cadran, cadran_items } }
//   - current_cadran == sent cadranNumber → refine (BE wants a narrower selection)
//   - current_cadran > sent cadranNumber  → next cadran
//   - cadran_items is empty / current_cadran is undefined → done (all 4 complete)
// Adjust field names below if the BE uses different keys.

function parseCadranPostResponse(
  sentCadranNumber: TrimaCadranNumber,
  data: any,
): TrimaCadranPostResponse {
  const reponses = data?.reponses;
  const cadranItems: any[] = reponses?.cadran_items ?? [];
  const sessionCompetenceId: number = reponses?.session_competence_id ?? 0;

  if (sentCadranNumber >= 4 || !cadranItems.length) {
    return { status: 'done' };
  }

  const nextCadranNumber = (sentCadranNumber + 1) as TrimaCadranNumber;
  const payload = parseCadranPayload(nextCadranNumber, cadranItems, sessionCompetenceId);
  return { status: 'next', payload };
}

export default useAPITrima;
